import {DeleteOutlined} from '@ant-design/icons';
import {Button, DatePicker, Form, Input} from 'antd';
import {FormListFieldData} from 'antd/es/form/FormList';
import React, {FC} from 'react';

import {CustomSelect} from 'components/form/inputs/custom-select';
import {DATE_DMY} from 'shared/constants/date-format';

import {
    BENEFIT_GOV_SUPPORT_OTHER_CODE, BINARY_VALUES,
    DOCUMENT_BASE_GOV_SUPPORT_OTHER_CODE
} from '../../model/project-investment-info-item-modal.constants';
import {requiredInputRule} from '../../utils';

interface IMainInfoBenefitItemProps {
    field: FormListFieldData;
    index: number;
    onRemove: (name: number) => void;
}

export const MainInfoBenefitItem: FC<IMainInfoBenefitItemProps> = ({
    field,
    index,
    onRemove,
}: IMainInfoBenefitItemProps) => {
    const isFirst = index === 0;
    const requiredRule = isFirst ? [requiredInputRule()] : [];

    return (
        <div className="project-item-block">
            {!isFirst && (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemove(field.name)}
                    className="delete-button"
                />
            )}
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'benefitTypeGovSupport']}
                    label="Форма государственной поддержки"
                    rules={requiredRule}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=benefit_type_gov_support',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prev = prevValues?.benefits?.[field.name]?.benefitTypeGovSupport;
                        const curr = currentValues?.benefits?.[field.name]?.benefitTypeGovSupport;
                        return prev !== curr;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const benefitTypeGovSupport = getFieldValue(['benefits', field.name, 'benefitTypeGovSupport']);
                        const isActive = benefitTypeGovSupport === BENEFIT_GOV_SUPPORT_OTHER_CODE;

                        return (
                            <Form.Item
                                name={[field.name, 'benefitTypeDescription']}
                                label="Наименование формы участия в инвестиционной деятельности"
                                rules={isActive && isFirst ? [requiredInputRule()] : []}
                            >
                                <Input disabled={!isActive} placeholder="Введите значение" autoComplete="off" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'benefitTypeBenefitDetail']}
                    label="Описание полученных льгот и преференций"
                    rules={requiredRule}
                >
                    <Input.TextArea placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name={[field.name, 'benefitBaseDocumentType']}
                    label="Вид документа-основания предоставления государственной поддержки"
                    rules={requiredRule}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=benefit_base_document_type',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prev = prevValues?.benefits?.[field.name]?.benefitBaseDocumentType;
                        const curr = currentValues?.benefits?.[field.name]?.benefitBaseDocumentType;
                        return prev !== curr;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const benefitBaseDocumentType = getFieldValue(['benefits', field.name, 'benefitBaseDocumentType']);
                        const isActive = benefitBaseDocumentType === DOCUMENT_BASE_GOV_SUPPORT_OTHER_CODE;

                        return (
                            <Form.Item
                                name={[field.name, 'benefitBaseDocumentTypeDescription']}
                                label="Наименование документа-основания"
                                rules={isActive && isFirst ? [requiredInputRule()] : []}
                            >
                                <Input placeholder="Введите значение" autoComplete="off" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'benefitBaseDocumentAuthority']}
                    label="Орган власти, утвердивший документ"
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name={[field.name, 'benefitBaseDocumentDate']}
                    label="Дата вступления документа-основания в силу"
                    rules={requiredRule}
                >
                    <DatePicker
                        className="date-picker"
                        placeholder="Введите значение"
                        format={DATE_DMY}
                    />
                </Form.Item>
                <Form.Item
                    name={[field.name, 'benefitBaseDocumentNumber']}
                    label="Номер документа-основания"
                    rules={requiredRule}
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'benefitBaseDocumentDetail']}
                    label="Дополнительные сведения к документу-основанию"
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name={[field.name, 'socialConstructionCommitment']}
                    label="Наличие обязательств по строительству социально значимых объектов"
                    rules={requiredRule}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=yes_no_investment_project',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prev = prevValues?.benefits?.[field.name]?.socialConstructionCommitment;
                        const curr = currentValues?.benefits?.[field.name]?.socialConstructionCommitment;
                        return prev !== curr;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const socialConstructionCommitment = getFieldValue(['benefits', field.name, 'socialConstructionCommitment']);
                        const isActive = socialConstructionCommitment === BINARY_VALUES.YES;

                        return (
                            <Form.Item
                                name={[field.name, 'socialConstructionCommitmentDescription']}
                                label="Описание обязательств по строительству социально значимых объектов"
                                rules={isActive && isFirst ? [requiredInputRule()] : []}
                            >
                                <Input.TextArea
                                    disabled={!isActive}
                                    placeholder="Введите значение"
                                    autoComplete="off"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
        </div>
    );
};
